import {
  insertParty,
  isOpenQuestion,
  PARTY_ROLE_LABELS,
  type Party,
  type PartyRole,
  partyAddressLabel,
  partyNeedsBankDetails,
  takeCandidate,
} from '@acasa/shared'
import { Fragment, useState } from 'react'
import { AgentPicker } from './agent-picker'
import { newId, removeItem, type SectionProps, updateItem } from './body-utils'
import { AddButton, EntryCard, Field, TextInput } from './fields'
import { PartyCandidates } from './party-candidates'
import { useDeletePhoto, useHandoverAgents, useHandoverContacts } from './use-handovers'
import { frozenIn } from './versions'

/**
 * The two sides of the handover. They get an add button and the onOffice
 * picker, and the last one of each cannot be removed: the PDF lays out a
 * signature block per side, and the completion check counts who is unnamed.
 */
const SIDES = ['handover', 'receiver'] as const

/** Local time formatted for `datetime-local`, which refuses an ISO Z string. */
function toLocalInput(iso: string | null): string {
  if (!iso) return ''
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''
  const offset = date.getTimezoneOffset() * 60_000
  return new Date(date.getTime() - offset).toISOString().slice(0, 16)
}

export function ObjectSection({ protocol, body, patch, readOnly }: SectionProps) {
  const { estate } = protocol
  const roleLabels = PARTY_ROLE_LABELS[protocol.kind]

  const address = [
    [estate.street, estate.houseNumber].filter(Boolean).join(' '),
    [estate.postalCode, estate.city].filter(Boolean).join(' '),
  ]
    .filter(Boolean)
    .join(', ')

  // Drafts only: a change to a completed protocol corrects the people already
  // in it, and offering the CRM's list again would spend quota for nothing.
  const contacts = useHandoverContacts(protocol.id, protocol.status === 'draft')
  const deletePhoto = useDeletePhoto(protocol.id)
  // The Makler list from Einstellungen. Not wanted on a read-only protocol,
  // which has no picker to fill.
  const configuredAgents = useHandoverAgents(!readOnly).data?.agents ?? []
  // The party whose removal is waiting for its second tap. Only ever a party
  // that has SIGNED — everyone else goes on the first.
  const [confirmingRemoval, setConfirmingRemoval] = useState<string | null>(null)

  const addParty = (role: PartyRole) =>
    patch((current) => ({
      ...current,
      parties: insertParty(current.parties, {
        id: newId(),
        role,
        name: '',
        address: '',
        phone: '',
        email: '',
        bankDetails: '',
        present: true,
        sourceAddressId: null,
      }),
    }))

  const isSigned = (party: Party) =>
    body.signatures.some((entry) => entry.partyId === party.id && entry.photoId)

  const canRemove = (party: Party) =>
    !readOnly &&
    (party.role === 'agent' ||
      party.role === 'witness' ||
      body.parties.filter((entry) => entry.role === party.role).length > 1)

  /**
   * Removing a party takes its signature with it — the entry in `signatures`
   * AND the captured image. Left behind, both are orphans: an entry pointing at
   * nobody, and a picture of somebody's signature on disk with no protocol
   * that shows it.
   *
   * The body change goes first. If deleting the image then fails, the protocol
   * is still right and only an unreferenced file is left over — the other way
   * round, a failed body save would leave a signature entry pointing at a
   * deleted image.
   */
  const removeParty = (party: Party) => {
    const photoIds = new Set(
      protocol.photos
        .filter((photo) => photo.section === 'signature' && photo.refKey === party.id)
        .map((photo) => photo.id),
    )
    for (const entry of body.signatures) {
      if (entry.partyId === party.id && entry.photoId) photoIds.add(entry.photoId)
    }

    patch((current) => ({
      ...current,
      parties: removeItem(current.parties, party.id),
      signatures: current.signatures.filter((entry) => entry.partyId !== party.id),
    }))
    setConfirmingRemoval(null)

    // A signature captured for an earlier Fassung stays on disk: it is part of
    // that version's PDF, and the server refuses to delete it (409).
    for (const photoId of photoIds) {
      const photo = protocol.photos.find((item) => item.id === photoId)
      if (photo && frozenIn(protocol, photo) !== null) continue
      deletePhoto.mutate(photoId)
    }
  }

  // The first and last party of each side. The add button sits after the
  // last — next to the people it adds to, and in document order — and so does
  // the picker, UNLESS it is a question: that goes above the first card,
  // because on a phone an empty card followed, a scroll later, by the reason
  // it is empty reads as a form that forgot to fill itself in.
  const firstOfSide = new Map(
    SIDES.map((role) => [body.parties.find((party) => party.role === role)?.id, role]),
  )
  const lastOfSide = new Map(
    SIDES.map((role) => [body.parties.findLast((party) => party.role === role)?.id, role]),
  )
  const candidates = contacts.data?.candidates ?? []
  const asksAbout = (role: (typeof SIDES)[number]) =>
    !readOnly && isOpenQuestion(role, candidates, body.parties)

  const picker = (role: (typeof SIDES)[number]) => (
    <PartyCandidates
      kind={protocol.kind}
      role={role}
      candidates={candidates}
      parties={body.parties}
      onTake={(candidate) =>
        patch((current) => ({
          ...current,
          parties: takeCandidate(current.parties, candidate, protocol.kind, newId),
        }))
      }
    />
  )

  const sideTools = (role: (typeof SIDES)[number]) =>
    readOnly ? null : (
      <div className="space-y-3">
        {!asksAbout(role) && picker(role)}
        <AddButton onClick={() => addParty(role)}>{roleLabels[role]}</AddButton>
      </div>
    )

  return (
    <div className="space-y-6">
      <section className="border-line bg-raised border p-4">
        <h3 className="text-faint mb-3 text-[10px] tracking-[0.14em] uppercase">Objekt</h3>

        <dl className="grid gap-x-6 gap-y-2 sm:grid-cols-2">
          {[
            ['Objekt-Nr.', estate.externalNumber ?? '—'],
            ['Adresse', address || '—'],
            ['Bezeichnung', estate.title ?? '—'],
            ['Objektart', estate.type ?? '—'],
            ['Vermarktung', estate.marketingType ?? '—'],
            ['Betreuer', estate.agent ?? '—'],
          ].map(([label, value]) => (
            <div key={label} className="flex gap-3 text-sm">
              <dt className="text-muted w-28 shrink-0 text-xs">{label}</dt>
              <dd className="text-ink min-w-0 flex-1">{value}</dd>
            </div>
          ))}
        </dl>

        {estate.fetchedLive && (
          <p className="text-faint mt-3 text-[11px]">
            Direkt aus onOffice geladen — dieses Objekt liegt nicht im lokalen Abzug (meist, weil es
            archiviert ist).
          </p>
        )}
      </section>

      <Field label="Datum und Uhrzeit der Übergabe">
        <TextInput
          type="datetime-local"
          disabled={readOnly}
          value={toLocalInput(body.handoverAt)}
          onChange={(event) =>
            patch((current) => ({
              ...current,
              handoverAt: event.target.value ? new Date(event.target.value).toISOString() : null,
            }))
          }
        />
      </Field>

      <div className="space-y-3">
        <h3 className="text-faint text-[10px] tracking-[0.14em] uppercase">Beteiligte</h3>

        {!readOnly && (contacts.isError || contacts.data?.available === false) && (
          <p className="text-faint text-[11px]">
            onOffice war nicht erreichbar — Beteiligte bitte von Hand eintragen.{' '}
            {/* The query never retries by itself (it costs onOffice quota, and a
                stairwell with no signal should not get four more attempts). But
                the failures seen in practice were single blips, and without
                this one of them costs the picker until the page is reloaded. */}
            <button
              type="button"
              disabled={contacts.isFetching}
              onClick={() => void contacts.refetch()}
              className="text-muted hover:text-ink -m-2 p-2 underline underline-offset-2 transition-colors disabled:opacity-50"
            >
              {contacts.isFetching ? 'wird geladen …' : 'Erneut versuchen'}
            </button>
          </p>
        )}

        {body.parties.map((party) => {
          const who = party.name.trim() || roleLabels[party.role]
          const confirming = confirmingRemoval === party.id
          const side = lastOfSide.get(party.id)
          const opens = firstOfSide.get(party.id)

          return (
            <Fragment key={party.id}>
              {opens && asksAbout(opens) && picker(opens)}
              <EntryCard
                title={
                  <span className="text-ink text-sm font-medium">{roleLabels[party.role]}</span>
                }
                removeAriaLabel={`${who} entfernen`}
                onRemove={
                  !canRemove(party) || confirming
                    ? undefined
                    : () => (isSigned(party) ? setConfirmingRemoval(party.id) : removeParty(party))
                }
              >
                {confirming && (
                  // Inline, not a dialog: the question belongs to this card, and the
                  // agent must see WHOSE signature is about to go.
                  <div
                    role="alert"
                    className="border-danger/30 bg-danger/5 mb-3 flex flex-wrap items-center gap-3 border p-3"
                  >
                    <p className="text-ink min-w-0 flex-1 basis-56 text-xs leading-relaxed">
                      <strong>{who}</strong> hat bereits unterschrieben. Mit der Person wird auch
                      die Unterschrift gelöscht — sie lässt sich nicht wiederherstellen.
                    </p>
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => setConfirmingRemoval(null)}
                        className="border-line text-muted hover:text-ink bg-surface min-h-11 border px-4 text-xs transition-colors"
                      >
                        Behalten
                      </button>
                      <button
                        type="button"
                        onClick={() => removeParty(party)}
                        aria-label={`${who} mit Unterschrift entfernen`}
                        className="bg-danger text-surface min-h-11 px-4 text-xs font-medium transition-colors hover:opacity-90"
                      >
                        Mit Unterschrift entfernen
                      </button>
                    </div>
                  </div>
                )}

                {party.role === 'agent' && !readOnly && (
                  <AgentPicker
                    agents={configuredAgents}
                    party={party}
                    onTake={(agent) =>
                      patch((current) => ({
                        ...current,
                        parties: updateItem(current.parties, party.id, {
                          name: agent.name,
                          address: agent.address,
                          phone: agent.phone,
                          email: agent.email,
                        }),
                      }))
                    }
                  />
                )}

                <div className="grid gap-3 sm:grid-cols-2">
                  <Field label="Name">
                    <TextInput
                      disabled={readOnly}
                      value={party.name}
                      autoComplete="name"
                      placeholder="Vor- und Nachname"
                      onChange={(event) =>
                        patch((current) => ({
                          ...current,
                          parties: updateItem(current.parties, party.id, {
                            name: event.target.value,
                          }),
                        }))
                      }
                    />
                  </Field>
                  <Field label="Telefon">
                    <TextInput
                      disabled={readOnly}
                      type="tel"
                      inputMode="tel"
                      value={party.phone}
                      onChange={(event) =>
                        patch((current) => ({
                          ...current,
                          parties: updateItem(current.parties, party.id, {
                            phone: event.target.value,
                          }),
                        }))
                      }
                    />
                  </Field>
                  <Field label="E-Mail">
                    <TextInput
                      disabled={readOnly}
                      type="email"
                      inputMode="email"
                      value={party.email}
                      onChange={(event) =>
                        patch((current) => ({
                          ...current,
                          parties: updateItem(current.parties, party.id, {
                            email: event.target.value,
                          }),
                        }))
                      }
                    />
                  </Field>
                  {/* On an Auszug this is the address the tenant is moving TO —
                  where the Kautionsabrechnung gets sent. */}
                  <Field label={partyAddressLabel(protocol.kind, party.role)}>
                    <TextInput
                      disabled={readOnly}
                      value={party.address}
                      onChange={(event) =>
                        patch((current) => ({
                          ...current,
                          parties: updateItem(current.parties, party.id, {
                            address: event.target.value,
                          }),
                        }))
                      }
                    />
                  </Field>

                  {partyNeedsBankDetails(protocol.kind, party.role) && (
                    <Field
                      label="Bankverbindung"
                      hint="IBAN und Kontoinhaber — für die Rückzahlung der Kaution."
                    >
                      <TextInput
                        disabled={readOnly}
                        value={party.bankDetails}
                        onChange={(event) =>
                          patch((current) => ({
                            ...current,
                            parties: updateItem(current.parties, party.id, {
                              bankDetails: event.target.value,
                            }),
                          }))
                        }
                      />
                    </Field>
                  )}
                </div>

                <label className="text-muted mt-3 flex items-center gap-2 text-xs">
                  <input
                    type="checkbox"
                    disabled={readOnly}
                    checked={party.present}
                    onChange={(event) =>
                      patch((current) => ({
                        ...current,
                        parties: updateItem(current.parties, party.id, {
                          present: event.target.checked,
                        }),
                      }))
                    }
                    className="accent-brand size-4"
                  />
                  bei der Übergabe anwesend
                </label>
              </EntryCard>
              {side && sideTools(side)}
            </Fragment>
          )
        })}

        {/* A side with nobody on it at all — only a hand-edited or very old
            draft — still gets its picker and add button. */}
        {SIDES.filter((role) => !body.parties.some((party) => party.role === role)).map((role) => (
          <Fragment key={role}>{sideTools(role)}</Fragment>
        ))}

        {!readOnly && (
          <div className="grid gap-2 sm:grid-cols-2">
            <AddButton onClick={() => addParty('witness')}>Zeuge</AddButton>
            <AddButton onClick={() => addParty('agent')}>weitere Person</AddButton>
          </div>
        )}
      </div>
    </div>
  )
}
